
import React, { useState, useMemo, useEffect } from "react";
import { useCart } from "../../contexts/CartContext";
import AddressForm from "../../components/checkout/AddressForm";
import StripeProvider from "../../components/checkout/StripeProvider";
import CheckoutForm from "../../components/checkout/CheckoutForm";
import {
  calculateTotalWeight,
  calculateShipping,
  calculateShippingByAddress,
} from "../../utils/shippingCalculations";
import {
  sendCustomerConfirmation,
  sendAdminNotification,
} from "../../services/emailService";
import { createShippingLabel } from "../../services/shippingService";
import { toast } from "sonner";
import { useFavorites } from "../../contexts/FavoritesContext";
import { useAuth } from "../../contexts/AuthContext";
import ProductPrice from "../product/ProductPrice";
import { formatPrice } from "../../utils/productPricing";

const priceClassName =
  '[&_.line-through]:!text-slate-500 [&_span.font-bold]:!text-amber-400 [&_span.rounded]:!bg-amber-400/10 [&_span.rounded]:!text-amber-300';

// Defined outside CartSidebar so its identity stays stable across re-renders —
// otherwise React remounts this whole subtree (and any focused input inside
// it) on every keystroke that updates CartSidebar's state.
const Panel = ({ onClose, children, closeDisabled }) => (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
    onClick={() => { if (!closeDisabled) onClose(); }}
  >
    <div
      className="absolute right-0 top-0 h-full w-full sm:max-w-sm flex flex-col bg-gradient-to-br from-slate-900 via-gray-900 to-black shadow-2xl border-l border-white/10"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Gold top accent line */}
      <div className="h-[3px] w-full bg-gradient-to-r from-amber-300 via-amber-400 to-yellow-500 flex-shrink-0" />
      {children}
    </div>
  </div>
);

const CartSidebar = ({ onClose }) => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState("address");
  const [shippingAddress, setShippingAddress] = useState({
    firstName: "", lastName: "", name: "", email: "",
    address: "", city: "", state: "", zipCode: "", country: "US",
  });
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [shippingError, setShippingError] = useState("");
  const [tax, setTax] = useState(0);
  const [taxCalculationId, setTaxCalculationId] = useState("");
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxError, setTaxError] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [canCancelPayment, setCanCancelPayment] = useState(false);
  const { toggleFavorite, isFavorite } = useFavorites();
  const { user, accessToken } = useAuth();

  useEffect(() => {
    if (!isProcessingPayment) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isProcessingPayment]);

  const subtotal = useMemo(() => getCartTotal(), [getCartTotal]);
  const shipping = useMemo(() => {
    if (subtotal >= 500) return 0;
    return shippingAddress.state
      ? calculateShippingByAddress(shippingAddress, cartItems, subtotal, selectedRate)
      : calculateShipping(cartItems, subtotal);
  }, [shippingAddress, cartItems, subtotal, selectedRate]);
  const total = useMemo(() => subtotal + shipping + tax, [subtotal, shipping, tax]);

  useEffect(() => {
    if (
      checkoutStep !== "payment" ||
      !selectedRate ||
      !shippingAddress.address ||
      !shippingAddress.city ||
      !shippingAddress.state ||
      !shippingAddress.zipCode
    ) {
      setTax(0);
      setTaxCalculationId("");
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setTaxLoading(true);
      setTaxError("");
      try {
        const response = await fetch("/api/calculate-tax", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({
            order: {
              subtotal,
              shipping_cost: shipping,
              shipping_address: shippingAddress,
            },
            items: cartItems.map((item) => ({
              product_id: item.id,
              quantity: item.quantity,
              selected_color: item.selectedColor || null,
            })),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          console.error("Tax API error:", {
            status: response.status,
            code: data.code,
            error: data.error,
          });
          throw new Error(data.error || "Unable to calculate sales tax");
        }
        setTax(Number(data.tax || 0));
        setTaxCalculationId(data.calculation_id);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Tax calculation failed:", error);
        setTax(0);
        setTaxCalculationId("");
        setTaxError(error.message);
      } finally {
        if (!controller.signal.aborted) setTaxLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [
    checkoutStep,
    selectedRate,
    shipping,
    shippingAddress,
    subtotal,
    cartItems,
  ]);

  const fetchShippingRates = async (address) => {
    setShippingError("");
    try {
      const response = await fetch("/api/shippo-rates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fromAddress: { name: "JmoLux Store", street1: "4533 Lost Mountain CT", city: "Powder Springs", state: "GA", zip: "30127", country: "US", email: "info@jmolux.com", phone: "4047160100" },
          toAddress: { name: address.name, street1: address.address, city: address.city, state: address.state, zip: address.zipCode, country: "US", email: address.email },
          parcel: { length: "10", width: "8", height: "4", distance_unit: "in", weight: calculateTotalWeight(cartItems), mass_unit: "lb" },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Shipping service error (${response.status})`);
      }
      if (data.rates && data.rates.length > 0) {
        setShippingRates(data.rates);
        setSelectedRate(data.rates[0]);
        return true;
      } else {
        throw new Error("No shipping rates available");
      }
    } catch (error) {
      console.error("Shipping calculation failed:", error);
      setShippingError(`Shipping calculation failed: ${error.message}`);
      if (error.message.includes("address could not be verified")) {
        setShippingRates([]);
        setSelectedRate(null);
        return false;
      }
      const fallbackRate = {
        object_id: "fallback",
        amount: calculateShippingByAddress(address, cartItems, subtotal, selectedRate).toString(),
        currency: "USD", provider: "Manual",
        servicelevel: { name: "Standard Shipping" },
      };
      setShippingRates([fallbackRate]);
      setSelectedRate(fallbackRate);
      return true;
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    setIsProcessingPayment(true);
    setCanCancelPayment(true);
    try {
      const orderData = {
        customer_email: shippingAddress.email || "customer@example.com",
        customer_name: shippingAddress.name,
        total, subtotal, shipping_cost: shipping, tax,
        tax_calculation_id: taxCalculationId,
        payment_intent_id: paymentIntent.id,
        shipping_address: shippingAddress,
        shipping_method: selectedRate,
        status: "processing",
      };

      const orderItems = cartItems.map((item) => ({
        product_id: item.id, product_name: item.name,
        product_category: item.category, price: item.price, quantity: item.quantity,
        selected_color: item.selectedColor, product_sku: item.sku, image: item.image,
      }));

      const orderHeaders = { "Content-Type": "application/json" };
      if (accessToken) {
        orderHeaders.Authorization = `Bearer ${accessToken}`;
      }
      const orderResponse = await fetch("/api/orders", {
        method: "POST",
        headers: orderHeaders,
        body: JSON.stringify({ order: orderData, items: orderItems }),
      });
      const orderPayload = await orderResponse.json().catch(() => ({}));
      if (!orderResponse.ok) {
        throw new Error(orderPayload.error || "Payment succeeded, but the order could not be saved.");
      }
      const order = orderPayload.order;
      const savedOrderItems = orderPayload.items || orderItems;

      setCanCancelPayment(false);
      try {
        await createShippingLabel(order, paymentIntent.id, accessToken);
      } catch (shippingError) {
        console.error("Shipping label error:", shippingError);
      }

      // These client-side confirmations are non-blocking. The shipper label
      // email is handled durably by the server-side background task.
      void Promise.allSettled([
        sendCustomerConfirmation(order, savedOrderItems),
        sendAdminNotification(order, savedOrderItems),
      ]);
      // clearCart updates the UI immediately; database synchronization should
      // not keep a paid customer trapped behind the processing overlay.
      void clearCart();
      setShowCheckout(false);
      onClose();
      toast.success(`Order #${order.id.slice(0, 8)} created successfully!`, {
        description: "Check your email to view your order details.",
        duration: 10000,
      });
    } catch (error) {
      console.error("Order submission failed:", error);
      toast.error("Order submission failed", { description: error.message, duration: 1000 });
    } finally {
      setIsProcessingPayment(false);
      setCanCancelPayment(false);
    }
  };

  // ── Checkout flow ──
  if (showCheckout) {
    return (
      <Panel onClose={onClose} closeDisabled={isProcessingPayment}>
        {isProcessingPayment && (
          <div className="absolute inset-0 bg-slate-900/95 flex items-center justify-center z-50">
            <div className="text-center px-6 max-w-xs">
              <div className="w-14 h-14 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <h3 className="text-base font-semibold text-white mb-1">Finalizing Your Order</h3>
              <p className="text-slate-400 text-xs">Payment approved. Saving your order details…</p>
              <div className="mt-3 flex items-center justify-center gap-1.5">
                {[0, 100, 200].map((d) => (
                  <div key={d} className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                ))}
              </div>
              <p className="mt-4 text-amber-400/90 text-[11px] font-medium">
                This should finish within a few moments.
              </p>
              {canCancelPayment && (
                <button onClick={() => { setIsProcessingPayment(false); setCanCancelPayment(false); }} className="mt-5 px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs hover:bg-red-500/20 transition-colors">
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5 sm:py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => checkoutStep === "payment" ? setCheckoutStep("address") : setShowCheckout(false)}
              disabled={isProcessingPayment}
              className="h-10 w-10 sm:h-8 sm:w-8 flex shrink-0 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-amber-400 hover:border-amber-400/60 transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <p className="text-[9px] uppercase tracking-[0.2em] text-amber-400/80 font-medium">
                Step {checkoutStep === "address" ? "1" : "2"} of 2
              </p>
              <h2 className="text-sm sm:text-[13px] font-bold text-white">
                {checkoutStep === "address" ? "Shipping Address" : "Payment"}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-amber-400" />
            <div className={`w-2 h-2 rounded-full transition-colors ${checkoutStep === "payment" ? "bg-amber-400" : "bg-white/15"}`} />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-x-hidden overflow-y-auto px-3.5 py-3.5 sm:px-5 sm:py-4 relative">
          {checkoutStep === "address" ? (
            <>
              <AddressForm
                address={shippingAddress}
                setAddress={setShippingAddress}
                onContinue={() => setCheckoutStep("payment")}
                fetchShippingRates={fetchShippingRates}
              />

              {shippingError && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-xs">{shippingError}</p>
                  <button onClick={() => fetchShippingRates(shippingAddress)} className="mt-1.5 text-red-400 underline text-xs">
                    Retry
                  </button>
                </div>
              )}

              {shippingRates.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs sm:text-[10px] uppercase tracking-wider text-amber-400/80 font-semibold mb-3">Shipping Options</p>
                  <div className="space-y-2">
                    {shippingRates.map((rate, i) => (
                      <label
                        key={rate.object_id || i}
                        className={`flex w-full min-w-0 items-center gap-3 p-3.5 sm:p-3 rounded-xl border cursor-pointer transition-all ${selectedRate?.object_id === rate.object_id ? "border-amber-400 bg-amber-400/10" : "border-white/10 bg-white/[0.03] hover:border-amber-400/30"}`}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <input type="radio" name="shipping" checked={selectedRate?.object_id === rate.object_id} onChange={() => setSelectedRate(rate)} className="h-4 w-4 shrink-0 accent-amber-400" />
                          <div className="min-w-0">
                            <p className="break-words text-sm sm:text-xs font-medium leading-snug text-slate-200">{rate.provider} — {rate.servicelevel?.name || "Standard"}</p>
                            {rate.estimated_days && <p className="text-xs sm:text-[10px] text-slate-500">{rate.estimated_days} days</p>}
                          </div>
                        </div>
                        <span className="shrink-0 text-right text-sm sm:text-xs font-bold text-amber-400">{formatPrice(parseFloat(rate.amount || rate.price || 0))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={isProcessingPayment ? "pointer-events-none opacity-40" : ""}>
                <div className="mb-4 p-3.5 bg-white/[0.03] border border-white/10 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[9px] uppercase tracking-wider text-amber-400/80 font-semibold">Shipping To</p>
                    <button onClick={() => setCheckoutStep("address")} className="text-[9px] text-amber-400 hover:text-amber-300 uppercase tracking-wider">Edit</button>
                  </div>
                  <p className="text-[11px] font-medium text-slate-200">{shippingAddress.name}</p>
                  <p className="mt-0.5 break-words text-[11px] leading-snug text-slate-500">{shippingAddress.address}, {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}</p>
                </div>

                {shippingRates.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs sm:text-[9px] uppercase tracking-wider text-amber-400/80 font-semibold mb-2">Shipping Method</p>
                    <div className="space-y-2">
                      {shippingRates.map((rate, i) => (
                        <label key={rate.object_id || i} className={`flex w-full min-w-0 items-start gap-3 p-3.5 sm:p-3 rounded-xl border cursor-pointer transition-all ${selectedRate?.object_id === rate.object_id ? "border-amber-400 bg-amber-400/10" : "border-white/10 bg-white/[0.03]"}`}>
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <input type="radio" name="payment-shipping" checked={selectedRate?.object_id === rate.object_id} onChange={() => setSelectedRate(rate)} className="mt-0.5 h-4 w-4 shrink-0 accent-amber-400" />
                            <div className="min-w-0">
                              <p className="break-words text-sm sm:text-[11px] font-medium leading-snug text-slate-200">{rate.provider} — {rate.servicelevel?.name || "Standard"}</p>
                              {rate.estimated_days && <p className="text-xs sm:text-[9px] text-slate-500">{rate.estimated_days} days</p>}
                            </div>
                          </div>
                          <span className="shrink-0 text-right text-sm sm:text-[11px] font-bold text-amber-400">{formatPrice(parseFloat(rate.amount || rate.price || 0))}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-5 bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02]">
                    <p className="text-[9px] uppercase tracking-wider text-amber-400/80 font-semibold">Order Summary</p>
                  </div>
                  <div className="px-4 py-3 space-y-1.5">
                    {cartItems.map((item) => (
                      <div key={item.cartId || item.id} className="flex min-w-0 items-start justify-between gap-3 text-[11px]">
                        <span className="min-w-0 break-words text-slate-400">{item.name}{item.selectedColor ? ` (${item.selectedColor.name})` : ""} ×{item.quantity}</span>
                        <span className="text-slate-300 shrink-0">{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t border-white/10 space-y-1.5">
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span>Subtotal</span><span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span className="min-w-0 pr-3">Shipping{selectedRate ? ` (${selectedRate.servicelevel?.name || selectedRate.provider})` : ""}</span>
                      <span className={shipping === 0 ? "text-green-400 font-medium" : ""}>{shipping === 0 ? "FREE" : formatPrice(shipping)}</span>
                    </div>
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span>Sales tax</span>
                      <span>
                        {taxLoading ? "Calculating…" : formatPrice(tax)}
                      </span>
                    </div>
                    <div className="flex justify-between text-[11px] font-bold text-white border-t border-white/10 pt-2 mt-1">
                      <span>Total</span><span className="text-amber-400">{formatPrice(total)}</span>
                    </div>
                  </div>
                </div>

                <StripeProvider>
                  <CheckoutForm
                    order={{
                      customer_email: shippingAddress.email,
                      customer_name: shippingAddress.name,
                      subtotal,
                      shipping_cost: shipping,
                      tax,
                      total,
                      tax_calculation_id: taxCalculationId,
                      shipping_address: shippingAddress,
                    }}
                    items={cartItems.map((item) => ({
                      product_id: item.id,
                      quantity: item.quantity,
                      selected_color: item.selectedColor || null,
                    }))}
                    onSuccess={handlePaymentSuccess}
                    disabled={
                      isProcessingPayment ||
                      taxLoading ||
                      Boolean(taxError) ||
                      !taxCalculationId
                    }
                  />
                  {taxError && (
                    <p className="mt-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
                      {taxError}
                    </p>
                  )}
                </StripeProvider>
              </div>
            </>
          )}
        </div>
      </Panel>
    );
  }

  // ── Cart view ──
  return (
    <Panel onClose={onClose}>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src="/img/Logo_Directory/crest_gold_real_transparent.png" alt="JmoLux" className="h-8 w-auto object-contain" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400/80 font-medium">Your</p>
            <h2 className="text-sm font-bold text-white leading-none">Shopping Cart</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cartItems.length > 0 && (
            <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-black">
              {cartItems.reduce((s, i) => s + i.quantity, 0)}
            </span>
          )}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {cartItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-slate-300">Your cart is empty</p>
            <p className="text-xs text-slate-500 mt-1">Add some products to get started</p>
            <button
              onClick={onClose}
              className="mt-5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 text-black text-xs font-bold uppercase tracking-wider hover:from-amber-300 transition-all"
            >
              Shop Now
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {cartItems.map((item) => {
              const itemId = item.cartId || item.id;
              return (
                <div key={itemId} className="flex gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10">
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-white/5 border border-white/10">
                    <img src={item.image} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-xs font-semibold text-white leading-snug line-clamp-2">{item.name}</h3>
                      <button
                        onClick={() => removeFromCart(itemId)}
                        className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Remove"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {item.selectedColor && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <div className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: item.selectedColor.hex }} />
                        <span className="text-[10px] text-slate-500">{item.selectedColor.name}</span>
                      </div>
                    )}

                    <ProductPrice product={item} size="sm" className={`mt-1 ${priceClassName}`} />

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center h-7 bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                        <button onClick={() => updateQuantity(itemId, Math.max(0, item.quantity - 1))} className="px-2.5 h-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors text-sm">−</button>
                        <span className="px-2.5 h-full flex items-center text-slate-200 text-xs font-semibold border-x border-white/10 min-w-[1.75rem] justify-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(itemId, item.quantity + 1)} className="px-2.5 h-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors text-sm">+</button>
                      </div>

                      <button onClick={() => toggleFavorite(item)} className={`flex items-center gap-1 text-[10px] uppercase tracking-wider transition-colors ${isFavorite(item.id) ? "text-amber-400" : "text-slate-500 hover:text-amber-400"}`}>
                        <svg className="w-3 h-3" fill={isFavorite(item.id) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.682l-1.318-1.364a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        {isFavorite(item.id) ? "Saved" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {cartItems.length > 0 && (
        <div className="flex-shrink-0 border-t border-white/10 bg-white/[0.02] px-5 py-4 space-y-3">
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-slate-400">
              <span>Subtotal ({cartItems.reduce((s, i) => s + i.quantity, 0)} items)</span>
              <span className="text-slate-300">{formatPrice(subtotal)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Total</span>
            <span className="text-xl font-bold text-amber-400">{formatPrice(getCartTotal())}</span>
          </div>

          <button
            onClick={() => setShowCheckout(true)}
            className="w-full h-11 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold text-xs uppercase tracking-wider hover:from-amber-300 hover:to-yellow-400 active:scale-[0.98] transition-all shadow-md shadow-amber-400/20 flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Proceed to Checkout
          </button>

          {user && (
            <button
              onClick={() => { onClose(); window.location.href = "/orders"; }}
              className="w-full h-10 rounded-xl border border-white/10 bg-white/[0.03] text-slate-400 text-xs uppercase tracking-wider font-semibold hover:bg-white/10 hover:text-white transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              View My Orders
            </button>
          )}
        </div>
      )}
    </Panel>
  );
};

export default CartSidebar;
